<template>
    <div v-if="cardOnrampMethod">
        <div v-if="provisioning" class="text-center py-5">
            <b-spinner variant="primary"></b-spinner>
            <div class="mt-3 text-secondary">
                Getting your card payment ready…
            </div>
            <div class="small text-muted mt-1">
                This usually takes just a few seconds
            </div>
        </div>

        <b-alert v-else-if="error" show variant="danger" class="mt-3">
            <div class="mb-2">
                <strong>Something went wrong.</strong>
                We couldn't set up your card payment right now. Please try again in a few moments.
            </div>
            <b-btn @click="provisionWallet" variant="outline-danger" size="sm">
                Try Again
            </b-btn>
        </b-alert>

        <template v-else-if="cardOnrampMethod.address">
            <div class="onramp-hero">
                <h4 class="onramp-hero-title">
                    Pay with Credit or Debit Card
                </h4>
                <p class="onramp-hero-sub">
                    VISA &amp; Mastercard accepted. We've set everything up for you —
                    plus a <strong>10% bonus</strong> on your credits.
                </p>
            </div>

            <!-- First-timers: explain the one-time verification. Returning verified buyers: skip it. -->
            <div v-if="!cardOnrampMethod.onrampCompleted" class="explainer-card">
                <div class="explainer-row">
                    <div class="explainer-icon explainer-icon-1">
                        <font-awesome-icon icon="fa-solid fa-id-card" />
                    </div>
                    <div>
                        <h6 class="explainer-title">A quick one-time verification</h6>
                        <p class="explainer-text">
                            Our secure payment partner will ask you to verify your identity
                            <strong>the first time only</strong> — this keeps your payment safe.
                            After that, paying by card takes seconds.
                        </p>
                    </div>
                </div>
                <div class="explainer-row">
                    <div class="explainer-icon explainer-icon-2">
                        <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" />
                    </div>
                    <div>
                        <h6 class="explainer-title">We've pre-filled everything</h6>
                        <p class="explainer-text">
                            The amount and the network are already set. The only thing you'll
                            enter is your wallet address — and we make that a single tap.
                        </p>
                    </div>
                </div>
            </div>

            <b-alert v-else show variant="success" class="onramp-ready">
                <font-awesome-icon icon="fa-solid fa-circle-check" class="mr-2" />
                You're all set — pick an amount below and pay by card.
            </b-alert>

            <!-- Step A: pick the amount, exactly like the crypto flow -->
            <div v-if="!price">
                <refill-prices :prices="creditRefillPricesCrypto" />
            </div>

            <!-- Step B: copy the address, then pay -->
            <template v-else>
                <div class="amount-summary">
                    <div>
                        <div class="amount-summary-label">You're buying</div>
                        <div class="amount-summary-credits">
                            {{ formatNumber(price.totalCredits) }} credits
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="amount-summary-price">${{ price.amount }}</div>
                        <b-btn variant="link" size="sm" class="change-amount" @click="changeAmount">
                            Change amount
                        </b-btn>
                    </div>
                </div>

                <div class="address-card">
                    <div class="address-card-header">
                        <div class="address-card-label">
                            <font-awesome-icon icon="fa-solid fa-wallet" class="mr-2" />
                            Your Wallet Address
                        </div>
                    </div>
                    <div class="address-display">
                        {{ cardOnrampMethod.address }}
                    </div>
                    <b-btn
                        block
                        size="lg"
                        variant="outline-primary"
                        @click="copyAddress"
                        class="copy-btn mt-3"
                    >
                        <font-awesome-icon
                            :icon="copied ? 'fa-light fa-check' : 'fa-light fa-copy'"
                            class="mr-2"
                        />
                        {{ copied ? 'Copied to clipboard!' : 'Copy Address' }}
                    </b-btn>
                </div>

                <h5 class="steps-heading">Just a few simple steps</h5>
                <div class="step-list">
                    <div class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-body">
                            <strong>Copy your wallet address</strong> using the button above.
                        </div>
                    </div>
                    <div class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-body">
                            Tap <strong>Pay with Card</strong> below — the amount and network
                            are already filled in for you.
                        </div>
                    </div>
                    <div class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-body">
                            When the payment page asks for your <strong>wallet address</strong>,
                            paste it (tap and hold, then choose <strong>Paste</strong>).
                        </div>
                    </div>
                    <div class="step-item">
                        <div class="step-number">4</div>
                        <div class="step-body">
                            Finish paying with your card. Your credits arrive
                            <strong>within a few minutes</strong>.
                        </div>
                    </div>
                </div>

                <b-alert v-if="copiedForPay" show variant="success" class="paste-reminder">
                    <font-awesome-icon icon="fa-solid fa-clipboard-check" class="mr-2" />
                    We copied your address. When the payment page asks for it, just paste.
                </b-alert>

                <b-btn
                    block
                    size="lg"
                    variant="primary"
                    pill
                    class="pay-btn"
                    @click="payWithCard"
                >
                    <font-awesome-icon icon="fa-solid fa-credit-card" class="mr-2" />
                    Pay with Card
                </b-btn>

                <div class="pay-tip">
                    <font-awesome-icon icon="fa-solid fa-lock" class="mr-1" />
                    Payments are processed securely by our card partner.
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import { mapState } from "vuex";

import RefillPrices from "@/components/wallet/refill/RefillPrices";

export default {
    data() {
        return {
            copied: false,
            copiedForPay: false,
            provisioning: false,
            error: false,
        };
    },
    mounted() {
        if (this.cardOnrampMethod && !this.cardOnrampMethod.address) {
            this.provisionWallet();
        }
    },
    computed: {
        ...mapState("wallet", ["price"]),
        ...mapState("paymentMethods", {
            paymentMethods: "methods",
        }),
        ...mapState("options", ["creditRefillPricesCrypto"]),
        cardOnrampMethod() {
            return this.paymentMethods?.find((pm) => pm.type === "cardOnramp");
        },
    },
    methods: {
        formatNumber(value) {
            return Number(value).toLocaleString();
        },
        provisionWallet() {
            this.error = false;
            this.provisioning = true;

            this.api
                .post("/nowpayments/createWallet", { type: "usdc-polygon" })
                .then(() => {
                    return this.$store.dispatch("paymentMethods/refresh");
                })
                .then(() => {
                    this.provisioning = false;
                })
                .catch(() => {
                    this.provisioning = false;
                    this.error = true;
                });
        },
        changeAmount() {
            this.$store.commit("wallet/setPrice", null);
            this.copiedForPay = false;
        },
        copyAddress() {
            navigator.clipboard.writeText(this.cardOnrampMethod.address);
            this.copied = true;

            setTimeout(() => {
                this.copied = false;
            }, 2200);
        },
        payWithCard() {
            // Auto-copy the address so it's already on the clipboard for the paste step,
            // then open the pre-filled onramp page via the platform handler (Cordova-safe).
            navigator.clipboard.writeText(this.cardOnrampMethod.address);
            this.copiedForPay = true;
            this.$store.dispatch("system/openBrowser", this.price.stripeOnrampUrl);
        },
    },
    components: {
        RefillPrices,
    },
};
</script>

<style scoped>
.onramp-hero {
    text-align: center;
    padding: 1.5rem 0 1rem;
}

.onramp-hero-title {
    font-weight: 700;
    letter-spacing: -0.01em;
    margin-bottom: 0.5rem;
}

.onramp-hero-sub {
    color: var(--secondary, #6c757d);
    font-size: 1rem;
    line-height: 1.5;
    max-width: 480px;
    margin: 0 auto;
}

.explainer-card {
    background: var(--white, #fff);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 14px;
    padding: 1rem 1.1rem;
    margin: 1rem 0 1.5rem;
}

body.dark .explainer-card {
    background: #2a2a2a;
    border-color: rgba(255, 255, 255, 0.08);
}

.explainer-row {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
}

.explainer-row + .explainer-row {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

body.dark .explainer-row + .explainer-row {
    border-top-color: rgba(255, 255, 255, 0.08);
}

.explainer-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    color: #fff;
}

.explainer-icon-1 {
    background: linear-gradient(135deg, #6610f2, #007bff);
}

.explainer-icon-2 {
    background: linear-gradient(135deg, #e8457c, #f4a261);
}

.explainer-title {
    font-weight: 700;
    margin-bottom: 0.25rem;
}

.explainer-text {
    font-size: 0.9rem;
    line-height: 1.5;
    color: var(--secondary, #6c757d);
    margin-bottom: 0;
}

.onramp-ready {
    margin: 1rem 0 1.25rem;
    font-weight: 600;
}

.amount-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.06), rgba(244, 162, 97, 0.06));
    border: 1px solid rgba(232, 69, 124, 0.18);
    border-radius: 14px;
    padding: 0.85rem 1.1rem;
    margin: 1rem 0;
}

.amount-summary-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--secondary, #6c757d);
}

.amount-summary-credits {
    font-size: 1.15rem;
    font-weight: 700;
}

.amount-summary-price {
    font-size: 1.3rem;
    font-weight: 700;
}

.change-amount {
    padding: 0;
    font-size: 0.8rem;
    font-weight: 600;
    text-decoration: none;
}

.address-card {
    background: linear-gradient(135deg, rgba(232, 69, 124, 0.06), rgba(244, 162, 97, 0.06));
    border: 1px solid rgba(232, 69, 124, 0.18);
    border-radius: 16px;
    padding: 1.25rem;
    margin: 1.25rem 0;
    box-shadow: 0 8px 30px rgba(232, 69, 124, 0.08);
}

.address-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.address-card-label {
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--secondary, #6c757d);
}

.address-display {
    font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 0.95rem;
    word-break: break-all;
    background: rgba(255, 255, 255, 0.65);
    border: 1px dashed rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    padding: 0.9rem 1rem;
    line-height: 1.5;
}

body.dark .address-display {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
    color: #eee;
}

.copy-btn {
    border-radius: 12px;
    font-weight: 600;
    letter-spacing: 0.01em;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
}

.steps-heading {
    margin-top: 1.75rem;
    margin-bottom: 1rem;
    font-weight: 600;
    text-align: center;
    letter-spacing: -0.01em;
}

.step-list {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-bottom: 1.25rem;
}

.step-item {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    background: var(--white, #fff);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 0.8rem 0.95rem;
}

body.dark .step-item {
    background: #2a2a2a;
    border-color: rgba(255, 255, 255, 0.08);
}

.step-number {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e8457c, #f4a261);
    color: #fff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.step-body {
    font-size: 0.92rem;
    line-height: 1.5;
    color: var(--secondary, #6c757d);
    padding-top: 0.2rem;
}

.step-body strong {
    color: inherit;
}

.paste-reminder {
    font-weight: 600;
}

.pay-btn {
    font-weight: 700;
    padding-top: 0.85rem;
    padding-bottom: 0.85rem;
}

.pay-tip {
    margin-top: 0.75rem;
    font-size: 0.85rem;
    color: var(--secondary, #6c757d);
    text-align: center;
}
</style>
